import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { SERVICES } from '../../api/endpoints';
import { toast } from 'react-toastify';
import { buildServiceRelationParams } from './serviceApiHelpers';

const row = (label, value) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
      {value ?? '—'}
    </Typography>
  </Grid>
);

const ViewService = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [service, setService] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(SERVICES.BY_ID(id), {
          params: buildServiceRelationParams(),
        });
        const payload = data?.data ?? data;
        if (!cancelled) setService(payload);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load service');
        navigate('/services');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (loading || !service) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/services')} sx={{ mb: 2 }}>
          Back to services
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {service.name ?? 'Service'}
          </Typography>
          <Button variant="outlined" size="small" onClick={() => navigate(`/services/edit/${id}`)}>
            Edit
          </Button>
        </Box>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {row('Name', service.name)}
              {row('Internal charges', service.internal_charges)}
              {row('External charges', service.external_charges)}
              {row('Status', service.status?.name ?? service.status?.label ?? service.status_name)}
              {row('Author', service.author?.name ?? service.author?.username)}
              {row('Created at', service.created_at)}
              {row('Description', service.description)}
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default ViewService;
